import {
  PACKAGES, RANKS, INITIAL_RIPPLE, RIPPLE_WAVE_RATE, RIPPLE_WAVE_TIERS,
  DROP_MATCH, CAP_PAYMENTS_PER_ORDER, DEFAULT_ADVANCED,
  packageById, volumeRankIndex, rankByIndex,
} from "./compPlan";

const MAX_DEPTH = 20;
const DM_LEVELS = 3;
const CURVE_POINTS = 120;              // resolution of members-vs-revenue curve
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Per-order cash rate to the beneficiary (the simulator user).
//   Initial Ripple : tree levels 1-5, one tier each (10/5/2.5/1.25/0.625%).
//                    User needs rank R(L) to earn tier L. Takes 1 slot.
//   Ripple Wave    : R6+ earns 1% on any downline order at tree levels 1-11.
//                    Number of tiers unlocked = rank - R5 (R6=1 ... R11=6).
//                    Multi-slot, subject to shared 3-payment cap. Top-of-tree
//                    assumption: the leader claims first pick of every tier
//                    they qualify for.
//   Deep Ripple    : tree levels 12-20 only, R12+. Rate = position in rank
//                    group (0.3/0.6/0.9%). One slot.
// Shared 3-payment cap: Direct + Rank + Check Match ≤ 3 per order.
function perOrderRates(L, R) {
  let directRate = 0, directSlot = 0;
  if (L >= 1 && L <= 5 && (R + 1) >= L) {
    directRate = INITIAL_RIPPLE[L - 1];
    directSlot = 1;
  }
  let slotsLeft = CAP_PAYMENTS_PER_ORDER - directSlot;
  let rankRate = 0, rankSlots = 0;
  if (L >= 1 && L <= 11 && slotsLeft > 0 && R >= 5) {  // R6 = index 5
    const rwTiers = Math.min(RIPPLE_WAVE_TIERS, R - 4);
    rankSlots = Math.min(rwTiers, slotsLeft);
    rankRate = rankSlots * RIPPLE_WAVE_RATE;
  }
  slotsLeft -= rankSlots;
  let cmRate = 0, cmSlot = 0;
  if (L >= 12 && L <= 20 && R >= 11 && slotsLeft >= 1) {
    const posInGroup = ((R - 11) % 3) + 1;
    cmRate = posInGroup * 0.003;
    cmSlot = 1;
  }
  return { directRate, rankRate, cmRate, rankSlots, total: directRate + rankRate + cmRate };
}

export function simulate({
  directs = 5,
  directPackage = "bronze",
  l2PerDirect = 2,
  l2Package = "bronze",
  l3PerL2 = 2,
  l3Package = "bronze",
  l4PerL3 = 2,
  l4Package = "bronze",
  l5PerL4 = 2,
  l5Package = "bronze",
  l6PerL5 = 2,
  l6Package = "bronze",
  l7PerL6 = 2,
  l7Package = "bronze",
  l8PerL7 = 2,
  l8Package = "bronze",
  l9PerL8 = 2,
  l9Package = "bronze",
  l10PerL9 = 2,
  l10Package = "bronze",
  downlinePackage = "bronze",
  myPackage = "bronze",
  duplicationPct = 30,
  duplicationMultiplier = DEFAULT_ADVANCED.duplicationMultiplier,
  branchingFactor = DEFAULT_ADVANCED.branchingFactor,
  retentionRate = DEFAULT_ADVANCED.retentionRate,
  // How many explicit levels the user has configured (0..10).
  // Levels beyond configuredLevels are ONLY included if calculateDeeper is true.
  configuredLevels = 10,
  calculateDeeper = true,
}) {
  const directPkg = packageById(directPackage);
  const l2Pkg = packageById(l2Package);
  const l3Pkg = packageById(l3Package);
  const l4Pkg = packageById(l4Package);
  const l5Pkg = packageById(l5Package);
  const l6Pkg = packageById(l6Package);
  const l7Pkg = packageById(l7Package);
  const l8Pkg = packageById(l8Package);
  const l9Pkg = packageById(l9Package);
  const l10Pkg = packageById(l10Package);
  const downPkg = packageById(downlinePackage);
  const myPkg = packageById(myPackage);

  const dupMult = clamp(duplicationMultiplier, 0.1, 2.0);
  const branch = clamp(branchingFactor, 0.5, 6.0);
  const retention = clamp(retentionRate, 0.3, 1.0);
  const dupRate = clamp((duplicationPct / 100) * dupMult, 0, 1);

  const ptsPerDollar = (pkg) => (pkg.price > 0 ? pkg.points / pkg.price : 0);

  // 10 explicit level configs. L1 uses absolute count (directs);
  // L2..L10 use per-parent multipliers.
  const explicitConfigs = [
    { pkg: directPkg, perParent: clamp(directs, 0, 200), isCountAbsolute: true },
    { pkg: l2Pkg,  perParent: clamp(l2PerDirect, 0, 20) },
    { pkg: l3Pkg,  perParent: clamp(l3PerL2, 0, 20) },
    { pkg: l4Pkg,  perParent: clamp(l4PerL3, 0, 20) },
    { pkg: l5Pkg,  perParent: clamp(l5PerL4, 0, 20) },
    { pkg: l6Pkg,  perParent: clamp(l6PerL5, 0, 20) },
    { pkg: l7Pkg,  perParent: clamp(l7PerL6, 0, 20) },
    { pkg: l8Pkg,  perParent: clamp(l8PerL7, 0, 20) },
    { pkg: l9Pkg,  perParent: clamp(l9PerL8, 0, 20) },
    { pkg: l10Pkg, perParent: clamp(l10PerL9, 0, 20) },
  ];
  const EXPLICIT_DEPTH = explicitConfigs.length;

  const levels = [];
  let prevCount = 0;
  for (let i = 0; i < MAX_DEPTH; i++) {
    const lvl = i + 1;
    let count = 0, pkg = downPkg;
    const included = lvl <= configuredLevels || (calculateDeeper && lvl > EXPLICIT_DEPTH);

    if (lvl <= EXPLICIT_DEPTH) {
      const cfg = explicitConfigs[i];
      pkg = cfg.pkg;
      if (cfg.isCountAbsolute) {
        count = cfg.perParent * retention;
      } else {
        count = prevCount * cfg.perParent * retention;
      }
    } else {
      pkg = downPkg;
      count = prevCount * dupRate * branch * retention;
    }

    levels.push({
      level: lvl,
      finalCount: included ? count : 0,
      price: pkg.price,
      pkgRankIdx: pkg.packageRankIdx,
      ptsPerDollar: ptsPerDollar(pkg),
      included,
    });
    prevCount = count;
  }
  levels.forEach((lv) => (lv.finalVolume = lv.finalCount * lv.price));

  const pkgRankIdx = myPkg.packageRankIdx;
  const startingShares = myPkg.points;

  // Highest included level index (0-based); at least 0 so chart has a range
  const maxIncludedIdx = (() => {
    let m = 0;
    for (let i = 0; i < MAX_DEPTH; i++) if (levels[i].included) m = i;
    return m;
  })();

  // Helper: compute totals when levels are sequentially filled to `depth`
  // (a fractional depth 0..MAX_DEPTH, where 5.3 means levels 1-5 fully + 30% of L6).
  // Uses sequential rank integration: each dollar of volume is paid at the
  // rank effective AT THAT MOMENT (volume earned before hitting R6 doesn't pay
  // Ripple Wave — the beneficiary wasn't R6 yet).
  const computeAtDepth = (depth) => {
    let members = 0;
    const vol = new Array(MAX_DEPTH).fill(0);
    const pts = new Array(MAX_DEPTH).fill(0);
    for (let i = 0; i < MAX_DEPTH; i++) {
      const levelFrac = clamp(depth - i, 0, 1);
      vol[i] = levels[i].finalVolume * levelFrac;
      pts[i] = vol[i] * levels[i].ptsPerDollar;
      members += levels[i].finalCount * levelFrac;
    }
    const cumQV = vol.reduce((a, b) => a + b, 0);
    const effR = Math.max(pkgRankIdx, volumeRankIndex(cumQV));

    let cash = 0;
    let cum = 0;
    for (let i = 0; i < MAX_DEPTH; i++) {
      const end = cum + vol[i];
      let pos = cum;
      while (pos < end - 1e-9) {
        const r = Math.max(pkgRankIdx, volumeRankIndex(pos));
        const nextThr = r + 1 < RANKS.length ? RANKS[r + 1].qv : Infinity;
        const chunk = Math.min(end, nextThr) - pos;
        const rates = perOrderRates(i + 1, r);
        cash += chunk * (rates.directRate + rates.rankRate + rates.cmRate);
        pos += chunk;
      }
      cum = end;
    }

    let dmShares = 0;
    for (let k = 0; k < DM_LEVELS; k++) {
      dmShares += pts[k] * DROP_MATCH[k];
    }
    return { members, cumQV, effR, cash, dmShares, vol, pts };
  };

  // Steady-state (fully-filled) numbers ------------------------------------
  const full = computeAtDepth(MAX_DEPTH);
  const finalRankIdx = full.effR;
  const finalRank = rankByIndex(finalRankIdx);

  // Per-stream cap-aware totals at steady-state, with sequential rank
  // integration so tier earnings only reflect the portion of volume that
  // arrived AFTER the beneficiary reached the tier's unlock rank.
  const irStream = new Array(5).fill(0);
  const rwStream = new Array(MAX_DEPTH).fill(0);
  const rwTierEarnings = new Array(RIPPLE_WAVE_TIERS).fill(0);
  const rwTierVolume = new Array(RIPPLE_WAVE_TIERS).fill(0);
  const dmStream = new Array(DM_LEVELS).fill(0);
  let drTotal = 0;
  {
    let cum = 0;
    for (let i = 0; i < MAX_DEPTH; i++) {
      const end = cum + full.vol[i];
      let pos = cum;
      while (pos < end - 1e-9) {
        const r = Math.max(pkgRankIdx, volumeRankIndex(pos));
        const nextThr = r + 1 < RANKS.length ? RANKS[r + 1].qv : Infinity;
        const chunk = Math.min(end, nextThr) - pos;
        const rates = perOrderRates(i + 1, r);
        const direct = chunk * rates.directRate;
        const rank = chunk * rates.rankRate;
        const cm = chunk * rates.cmRate;
        if (i < 5) irStream[i] += direct;
        rwStream[i] += rank;
        for (let k = 0; k < (rates.rankSlots || 0); k++) {
          rwTierEarnings[k] += chunk * RIPPLE_WAVE_RATE;
          rwTierVolume[k] += chunk;
        }
        drTotal += cm;
        pos += chunk;
      }
      cum = end;
    }
  }
  for (let k = 0; k < DM_LEVELS; k++) {
    dmStream[k] = full.pts[k] * DROP_MATCH[k];
  }

  const totalCash = full.cash;
  const totalQV = full.cumQV;
  const earnedShares = full.dmShares;
  const totalShares = startingShares + earnedShares;

  // Growth curve (members vs revenue) --------------------------------------
  // Sample from 0..(maxIncludedIdx+1) sequential fill in CURVE_POINTS steps.
  // If deeper is off, curve stops at configuredLevels.
  const curveMaxDepth = maxIncludedIdx + 1;
  const growthCurve = [];
  for (let s = 0; s <= CURVE_POINTS; s++) {
    const depth = (s / CURVE_POINTS) * curveMaxDepth;
    const p = computeAtDepth(depth);
    growthCurve.push({
      members: p.members,
      cash: p.cash,
      qv: p.cumQV,
      shares: startingShares + p.dmShares,
      rank: rankByIndex(p.effR).rank,
    });
  }

  // Breakdowns for cards ---------------------------------------------------
  const irBreakdown = [];
  for (let i = 0; i < 5; i++) {
    const L = i + 1;
    irBreakdown.push({
      label: `IR${L}`, level: L, ratePct: INITIAL_RIPPLE[i] * 100,
      earnings: irStream[i], potential: full.vol[i] * INITIAL_RIPPLE[i],
      unlocked: (finalRankIdx + 1) >= L,
      members: levels[i].finalCount, volume: full.vol[i],
    });
  }

  const rwUnlockedTiers = Math.min(RIPPLE_WAVE_TIERS, Math.max(0, finalRankIdx - 4));
  // Volume across L1..L11 for illustrative potential-per-tier
  const l1_11Volume = levels.slice(0, 11).reduce((sum, lv) => sum + lv.finalVolume, 0);
  const rwBreakdown = [];
  for (let k = 0; k < RIPPLE_WAVE_TIERS; k++) {
    const unlockRank = 6 + k; // R6..R11
    rwBreakdown.push({
      label: `RW${k + 1}`,
      level: unlockRank,
      tag: `Unlocks at R${unlockRank} · 1% per downline order`,
      ratePct: RIPPLE_WAVE_RATE * 100,
      earnings: rwTierEarnings[k],
      potential: l1_11Volume * RIPPLE_WAVE_RATE,
      unlocked: k < rwUnlockedTiers,
      members: 0,
      volume: rwTierVolume[k],
    });
  }
  const rwTotal = rwStream.reduce((a, b) => a + b, 0);

  const dmBreakdown = [];
  for (let k = 0; k < DM_LEVELS; k++) {
    dmBreakdown.push({
      label: `DM${k + 1}`, level: k + 1, ratePct: DROP_MATCH[k] * 100,
      shares: dmStream[k],
      members: levels[k].finalCount,
    });
  }

  let drGroup = null;
  if (finalRankIdx >= 11 && finalRankIdx <= 13) drGroup = "G1";
  else if (finalRankIdx >= 14 && finalRankIdx <= 16) drGroup = "G2";
  else if (finalRankIdx >= 17) drGroup = "G3";
  const drPotential = { G1: totalQV * 0.003, G2: totalQV * 0.006, G3: totalQV * 0.009 };

  // Team commissions: total cash earned by every member below the top leader.
  // Each parent at level p runs the same engine on their own subtree (uniform-
  // tree assumption). Their subtree at their own level k has vol = vol[p+k] /
  // n[p], and they inherit their package rank as the starting rank floor.
  let teamCash = 0;
  for (let p = 0; p < MAX_DEPTH - 1; p++) {
    const parentCount = levels[p].finalCount;
    if (parentCount <= 1e-9) continue;
    const parentPkgRankIdx = levels[p].pkgRankIdx;
    let cashPerParent = 0;
    let cum = 0;
    for (let k = 1; k < MAX_DEPTH - p; k++) {
      const sv = levels[p + k].finalVolume / parentCount;
      if (sv <= 0) continue;
      const end = cum + sv;
      let pos = cum;
      while (pos < end - 1e-9) {
        const r = Math.max(parentPkgRankIdx, volumeRankIndex(pos));
        const nextThr = r + 1 < RANKS.length ? RANKS[r + 1].qv : Infinity;
        const chunk = Math.min(end, nextThr) - pos;
        const rates = perOrderRates(k, r);
        cashPerParent += chunk * (rates.directRate + rates.rankRate + rates.cmRate);
        pos += chunk;
      }
      cum = end;
    }
    teamCash += cashPerParent * parentCount;
  }

  const nextRank = RANKS.slice(finalRankIdx + 1).find((r) => r.qv > totalQV) || null;
  const irTotal = irStream.reduce((a, b) => a + b, 0);

  return {
    inputs: {
      directs, directPkg,
      l2Pkg, l3Pkg, l4Pkg, l5Pkg, l6Pkg, l7Pkg, l8Pkg, l9Pkg, l10Pkg,
      downPkg, myPkg,
      duplicationPct, configuredLevels, calculateDeeper,
      duplicationMultiplier: dupMult, branchingFactor: branch, retentionRate: retention,
    },
    tree: levels.map((lv) => ({
      level: lv.level, members: lv.finalCount, avgPrice: lv.price, volume: lv.finalVolume,
      included: lv.included,
    })),
    growthCurve,
    totalQV,
    rank: finalRank,
    startingRank: {
      rank: pkgRankIdx >= 0 ? `R${pkgRankIdx + 1}` : "R0",
      package: myPkg.name,
    },
    nextRank,
    initialRipple: { breakdown: irBreakdown, total: irTotal, unlockedTiers: Math.min(5, Math.max(0, finalRankIdx + 1)), maxFromPackage: Math.max(0, pkgRankIdx + 1) },
    rippleWave:    { breakdown: rwBreakdown, total: rwTotal, unlockedTiers: rwUnlockedTiers },
    deepRipple:    { unlockedGroup: drGroup, potentialByGroup: drPotential, total: drTotal },
    dropMatch:     { breakdown: dmBreakdown, sharesTotal: earnedShares },
    shares: {
      startingShares, startingBase: startingShares, startingBonus: 0,
      earnedShares, totalShares,
    },
    totals: {
      totalCash,
      teamCash,
      orgTotalCash: totalCash + teamCash,
      dropMatchShares: earnedShares, startingShares, totalShares,
    },
  };
}

export const fmtMoney = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(Math.round(n || 0));
export const fmtMoneyDetail = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(n || 0);
export const fmtNumber = (n) => new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
}).format(n || 0);

export { PACKAGES, RANKS };
